import camelCase from 'lodash.camelcase';

import { FeatureFlagDeclarations } from './feature-flag-declarations';

export class FeatureFlags {
  /**
   * @param {object} [state]
   * @param {object} [featureFlags]
   */
  constructor({ state, featureFlags }) {
    if ((state || featureFlags) && !!state === !!featureFlags) {
      throw new Error('Must have state or feature flags, but not both!');
    }

    this.featureFlags = state?.featureFlags || featureFlags;
  }

  areFeatureFlagsReady() {
    return !!this.featureFlags?.isLDReady;
  }

  getFeatureFlag(featureFlag) {
    if (this.areFeatureFlagsReady()) {
      const ffKey = camelCase(featureFlag.key);
      return this.featureFlags?.[ffKey] ?? featureFlag.defaultValue;
    }
    return featureFlag.defaultValue;
  }

  get enableAddOnCentral() {
    const featureFlag = FeatureFlagDeclarations.enableAddOnCentral;
    return this.getFeatureFlag(featureFlag);
  }

  get isCheapestRateEnabled() {
    const featureFlag = FeatureFlagDeclarations.enableCheapestRate;
    return this.getFeatureFlag(featureFlag);
  }

  get enableV3Hotkeys() {
    const featureFlag = FeatureFlagDeclarations.enableV3Hotkeys;
    return this.getFeatureFlag(featureFlag);
  }

  get enableNewShippingServiceSelector() {
    const featureFlag = FeatureFlagDeclarations.enableNewShippingServiceSelector;
    return this.getFeatureFlag(featureFlag);
  }

  get enableV3AsyncLabelPrinting() {
    const featureFlag = FeatureFlagDeclarations.enableV3AsyncLabelPrinting;
    return this.getFeatureFlag(featureFlag);
  }

  get enableDelegationDate() {
    return this.getFeatureFlag(FeatureFlagDeclarations.enableDelegationDate);
  }

  get enableInteractiveReloadButtonOnOrdersGrid() {
    const featureFlag = FeatureFlagDeclarations.enableInteractiveReloadButtonOnOrdersGrid;
    return this.getFeatureFlag(featureFlag);
  }

  get enableCswRatePayloadDiff() {
    const featureFlag = FeatureFlagDeclarations.enableCswRatePayloadDiff;
    return this.getFeatureFlag(featureFlag);
  }

  get primaryPusherApp() {
    const featureFlag = FeatureFlagDeclarations.primaryPusherApp;
    return this.getFeatureFlag(featureFlag);
  }

  get parcelGuardEnabled() {
    const featureFlag = FeatureFlagDeclarations.parcelGuardEnabled;
    return this.getFeatureFlag(featureFlag);
  }

  get enableOrderOverview() {
    const featureFlag = FeatureFlagDeclarations.enableOrderOverview;
    return this.getFeatureFlag(featureFlag);
  }

  get shouldBypassBatchesGridRefresh() {
    const featureFlag = FeatureFlagDeclarations.enableCreateLabelBatchesNavRefreshBypass;
    return this.getFeatureFlag(featureFlag);
  }

  get enableSuncoAuthorization() {
    const featureFlag = FeatureFlagDeclarations.enableSuncoAuthorization;
    return this.getFeatureFlag(featureFlag);
  }

  get enableDeliverByDate() {
    const featureFlag = FeatureFlagDeclarations.enableDeliverByDate;
    return this.getFeatureFlag(featureFlag);
  }

  get enablePremiumProgramTags() {
    return this.getFeatureFlag(FeatureFlagDeclarations.enablePremiumProgramTags);
  }

  get enableShopPromiseTag() {
    const featureFlag = FeatureFlagDeclarations.enableShopPromiseTag;

    return this.getFeatureFlag(featureFlag);
  }

  get enableFedexRateComparison() {
    return this.getFeatureFlag(FeatureFlagDeclarations.enableFedexRateComparison);
  }

  get enableUspsRateComparison() {
    return this.getFeatureFlag(FeatureFlagDeclarations.enableUspsRateComparison);
  }

  get enableAsyncMarketplaceNotifications() {
    const featureFlag = FeatureFlagDeclarations.enableAsyncMarketplaceNotifications;

    return this.getFeatureFlag(featureFlag);
  }

  get enableAsyncSendNotifications() {
    const featureFlag = FeatureFlagDeclarations.enableAsyncSendNotifications;

    return this.getFeatureFlag(featureFlag);
  }

  get enableNewOrderImport() {
    const featureFlag = FeatureFlagDeclarations.enableNewOrderImport;
    return this.getFeatureFlag(featureFlag);
  }

  get enableDangerousGoods() {
    const featureFlag = FeatureFlagDeclarations.enableDangerousGoods;
    return this.getFeatureFlag(featureFlag);
  }

  get enableCompareRateOnLoad() {
    const featureFlag = FeatureFlagDeclarations.enableCompareRateOnLoad;
    return this.getFeatureFlag(featureFlag);
  }

  get enableCompareUpsByoaToDap() {
    const featureFlag = FeatureFlagDeclarations.enableCompareUpsByoaToDap;
    return this.getFeatureFlag(featureFlag);
  }

  get enableGlobalPostRateComparison() {
    return this.getFeatureFlag(FeatureFlagDeclarations.enableGlobalPostRateComparison);
  }

  get enableParcelGuardRateComparison() {
    return this.getFeatureFlag(FeatureFlagDeclarations.enableParcelGuardRateComparison);
  }

  get enableAlternativeRatesShippingServiceRecommendations() {
    return this.getFeatureFlag(FeatureFlagDeclarations.enableAlternativeRatesShippingServiceRecommendations);
  }

  get enableAlternativeRatesInsuranceRecommendations() {
    return this.getFeatureFlag(FeatureFlagDeclarations.enableAlternativeRatesInsuranceRecommendations);
  }

  get enableAlternativeRatesRateShopperRecommendations() {
    return this.getFeatureFlag(FeatureFlagDeclarations.enableAlternativeRatesRateShopperRecommendations);
  }

  get enableAlternativeRatesInOrderGrid() {
    return this.getFeatureFlag(FeatureFlagDeclarations.enableAlternativeRatesInOrderGrid);
  }

  get shouldSendV3QueueServiceDownloadUrl() {
    return this.getFeatureFlag(FeatureFlagDeclarations.shouldSendV3QueueServiceDownloadUrl);
  }

  get enableLegacyAlternativeRatesFallback() {
    return this.getFeatureFlag(FeatureFlagDeclarations.enableLegacyAlternativeRatesFallback);
  }

  get enableBulkRefreshRatesOnSuperSelect() {
    return this.getFeatureFlag(FeatureFlagDeclarations.enableBulkRefreshRatesOnSuperSelect);
  }

  get enableFedexWalletServiceMappings() {
    return this.getFeatureFlag(FeatureFlagDeclarations.enableFedexWalletServiceMappings);
  }

  get enableCostReviewModalRefactor() {
    return this.getFeatureFlag(FeatureFlagDeclarations.enableCostReviewModalRefactor);
  }

  get enableAmazonBuyShippingAlternativeRates() {
    return this.getFeatureFlag(FeatureFlagDeclarations.enableAmazonBuyShippingAlternativeRates);
  }

  get enableEnhancedReturnsErrors() {
    return this.getFeatureFlag(FeatureFlagDeclarations.enableEnhancedReturnsErrors);
  }

  get enableSierraChat() {
    return this.getFeatureFlag(FeatureFlagDeclarations.enableSierraChat);
  }

  get enableSingleFulfillmentPlanOrdersGridSidebarDrawerRedesign() {
    return this.getFeatureFlag(FeatureFlagDeclarations.enableSingleFulfillmentPlanOrdersGridSidebarDrawerRedesign);
  }

  get enableUpsLabelTermsV2() {
    return this.getFeatureFlag(FeatureFlagDeclarations.enableUpsLabelTermsV2);
  }

  get enableSaveShippingConfigServiceCollectionSync() {
    return this.getFeatureFlag(FeatureFlagDeclarations.enableSaveShippingConfigServiceCollectionSync);
  }

  get enableNewHtmlEditorForPackingSlips() {
    return this.getFeatureFlag(FeatureFlagDeclarations.enableNewHtmlEditorForPackingSlips);
  }
}
